import {
  PromptElementController,
  NumericConstraint,
  NumericRangeConstraint,
  DateConstraint,
  AbstractConstraintType,
} from '@sas-dvr/ltjs-bird';
import { StyleAttribute, StyleChain } from '@sas-dvr/ltjs-commons';
import { getAllTextStyles, getStyleColorString } from '@sas-dvr/ltjs-commons/util/StyleUtils';
import { StyleData, StyleElement } from '@sas-dvr/internal-va-react-core/store/style/StyleState';
// import { CSSProperties } from 'react';

export function getConstraint(controller: PromptElementController): AbstractConstraintType | null {
  const model = controller.getModel();
  if (!model) {
    return null;
  }

  const promptDefinition = model.getPromptDefinition();
  if (!promptDefinition) {
    return null;
  }

  return promptDefinition.getConstraint();
}

export function getRequired(controller: PromptElementController): boolean {
  const constraint = getConstraint(controller);
  if (constraint) {
    return constraint.getRequired();
  }

  return false;
}

export function getIsNumeric(controller: PromptElementController): boolean {
  const column = controller.getColumn();
  if (column) {
    return column.isNumber() && !column.isDateTime();
  }

  const constraint = getConstraint(controller);
  return constraint instanceof NumericConstraint || constraint instanceof NumericRangeConstraint;
}

export function getIsDateTime(controller: PromptElementController): boolean {
  const column = controller.getColumn();
  if (column) {
    return column.isDateTime();
  }

  const constraint = getConstraint(controller);
  return constraint instanceof DateConstraint;
}

interface PromptData {
  label: string;
  values: string[];
  measureValues?: string[];
}

export function getFormattedData(controller: PromptElementController): PromptData {
  const data: PromptData = {
    label: controller.getLabel() || '',
    values: [],
  };

  const column = controller.getColumn();
  const uniqueRowIndices = controller.getUniqueRowIndicies();
  if (!column || !uniqueRowIndices) {
    return data;
  }

  data.values = uniqueRowIndices.map((index) => {
    const dataValue = column.getDataValueAtIndex(index, true);
    if (!dataValue) {
      return '';
    }
    if (dataValue.isMissing()) {
      return PromptElementController.getMissingDisplayString() || '';
    }
    return dataValue.getFormattedValue() || dataValue.getStringValue() || '';
  });

  if (controller.getMeasureColumn()) {
    data.measureValues = uniqueRowIndices.map((index) => {
      return controller.getFormattedMeasure(index) || '';
    });
  }

  return data;
}

export function getPromptStyleOverrides(controller: PromptElementController): StyleData {
  const styleChain = controller.getStyleChain();
  if (!styleChain) return {};

  const overrides = getTextStyles(styleChain);

  // Selected overrides are the same except for color
  const selected: StyleElement['selected'] = {};
  if (styleChain.hasStyleAttributeValueAboveBase(StyleAttribute.SELECTED_BACKGROUND_COLOR))
    selected.bg = getStyleColorString(styleChain, StyleAttribute.SELECTED_BACKGROUND_COLOR, 1);
  if (styleChain.hasStyleAttributeValueAboveBase(StyleAttribute.SELECTED_COLOR))
    selected.fg = getStyleColorString(styleChain, StyleAttribute.SELECTED_COLOR, 1);

  return {
    ...overrides,
    selected,
  };
}

// TODO: This is useful to various components, so move it out of promptland
export function getTextStyles(styleChain: StyleChain): StyleData | undefined {
  const styleBundle = getAllTextStyles(styleChain);
  if (!styleBundle) {
    return;
  }
  const font: StyleElement['font'] = {};
  if (styleBundle['font-family']) {
    font.fontFamily = styleBundle['font-family'];
  }
  if (styleBundle['font-size']) {
    font.fontSize = styleBundle['font-size'];
  }
  if (styleBundle['font-style']) {
    font.fontStyle = styleBundle['font-style'];
  }
  if (styleBundle['font-weight']) {
    font.fontWeight = styleBundle['font-weight']; // FIXME: type error as CSSProperties['fontWeight'];
  }
  if (styleBundle['text-decoration']) {
    font.textDecoration = styleBundle['text-decoration'];
  }
  const normal: { bg?: string; fg?: string } = {};
  if (styleBundle['background-color']) {
    normal.bg = styleBundle['background-color'];
  }
  if (styleBundle['color']) {
    normal.fg = styleBundle['color'];
  }

  return {
    font,
    normal,
  };
}
